import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Flex, Loader } from '@mantine/core';
import { MessageListItem } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/MessageListItem/MessageListItem';
import { MessagesFilter } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/MessagesFilter/MessagesFilter';
import {
	MessagesListContainer,
	VirtualRowContainer,
	MessagesListScrollContainer,
	VirtualizerContainer,
} from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/MessagesList/MessagesList.styled';
import { useDebounceValue } from '@dealstack/frontend/shared/other/react/hooks/useDebounceValue.ts';
import { MessagesListEmpty } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/MessagesListEmpty/MessagesListEmpty';
import { useInfiniteScroll } from '@dealstack/frontend/app/pages/Project/MIP/Messages/components/MessagesList/hooks/useInfiniteScroll';
import { useMessages } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useMessages';
import { useTranslationMipMessages } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { createMessagesFilter } from './MessagesList.utils';
import { DateValue } from '@mantine/dates';
import { INVESTOR_MESSAGES_TEST_ID } from '@dealstack/shared/tests/testElementIds';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { getUserFacingErrorMessage } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';

const ESTIMATED_ROW_HEIGHT = 90;

interface Props {
	investorId: string;
	messageId: string | null;
	setMessageId: (messageId: string | null) => void;
}

export const MessagesList = ({ investorId, messageId, setMessageId }: Props) => {
	const { mipId } = useRequiredParams('mipId');
	const tMipMessages = useTranslationMipMessages();
	const [searchParams] = useSearchParams();
	const investmentWorkflowId = searchParams.get('investmentWorkflowId');
	const documentWorkflowInstanceId = searchParams.get('documentWorkflowInstanceId');
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedDates, setSelectedDates] = useState<[DateValue, DateValue]>([null, null]);
	const debouncedSearchQuery = useDebounceValue(searchQuery, 500);

	const filters = createMessagesFilter({
		searchQuery: debouncedSearchQuery,
		selectedDates,
		investmentWorkflowId,
		documentWorkflowInstanceId,
	});

	const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useMessages({
		investorId,
		mipId,
		filters,
	});

	useEffect(() => {
		if (data?.messages.length) {
			setMessageId(data.messages[0].id);
		}
	}, [data]);

	const messages = data?.messages ?? [];
	const total = data?.total ?? 0;
	const dateOfFirstMessage = data?.dateOfFirstMessage;

	const parentRef = useRef<HTMLDivElement>(null);
	const rowVirtualizer = useVirtualizer({
		count: messages.length + (hasNextPage ? 1 : 0), // add 1 for loading indicator
		getScrollElement: () => parentRef.current,
		estimateSize: () => ESTIMATED_ROW_HEIGHT,
		overscan: 5,
	});

	// fetch next page when scrolling near the end
	useInfiniteScroll({
		virtualizer: rowVirtualizer,
		messages,
		hasNextPage: !!hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
	});

	if (isLoading) {
		return (
			<MessagesListContainer direction="column">
				<MessagesFilter
					searchText={searchQuery}
					selectedDates={selectedDates}
					onSearchQueryChange={setSearchQuery}
					onDateRangeChange={setSelectedDates}
					dateOfFirstMessage={dateOfFirstMessage}
				/>
				<Flex justify="center" align="center" h="200px">
					<Loader
						size="sm"
						color="black"
						data-testid={INVESTOR_MESSAGES_TEST_ID.MESSAGE_LIST_LOADING}
					/>
				</Flex>
			</MessagesListContainer>
		);
	}

	if (error) {
		return (
			<MessagesListContainer direction="column">
				<MessagesFilter
					searchText={searchQuery}
					selectedDates={selectedDates}
					onSearchQueryChange={setSearchQuery}
					onDateRangeChange={setSelectedDates}
					dateOfFirstMessage={dateOfFirstMessage}
				/>
				<Flex justify="center" align="center" h="200px">
					{`${tMipMessages('list.error')} ${getUserFacingErrorMessage(error) ?? tMipMessages('list.error_unknown')}`}
				</Flex>
			</MessagesListContainer>
		);
	}

	return (
		<MessagesListContainer
			direction="column"
			data-testid={INVESTOR_MESSAGES_TEST_ID.MESSAGE_LIST_CONTAINER}
		>
			<MessagesFilter
				searchText={searchQuery}
				selectedDates={selectedDates}
				onSearchQueryChange={setSearchQuery}
				onDateRangeChange={setSelectedDates}
				dateOfFirstMessage={dateOfFirstMessage}
			/>
			{!total ? (
				<MessagesListEmpty />
			) : (
				<MessagesListScrollContainer direction="column" ref={parentRef}>
					<VirtualizerContainer $totalSize={rowVirtualizer.getTotalSize()}>
						<VirtualRowContainer
							$start={rowVirtualizer.getVirtualItems()[0]?.start ?? 0}
							data-testid={INVESTOR_MESSAGES_TEST_ID.MESSAGE_LIST}
						>
							{rowVirtualizer.getVirtualItems().map((virtualRow) => {
								const isLoaderRow = virtualRow.index > messages.length - 1;
								const message = messages[virtualRow.index];

								return isLoaderRow ? (
									<Flex justify="center" align="center" h="100%">
										{isFetchingNextPage && (
											<Loader
												size="sm"
												color="black"
												data-testid={
													INVESTOR_MESSAGES_TEST_ID.MESSAGE_LIST_LOADING
												}
											/>
										)}
									</Flex>
								) : (
									<MessageListItem
										key={message?.id}
										index={virtualRow.index}
										message={message}
										selected={messageId === message?.id}
										onSelect={() => setMessageId(message?.id || null)}
									/>
								);
							})}
						</VirtualRowContainer>
					</VirtualizerContainer>
				</MessagesListScrollContainer>
			)}
		</MessagesListContainer>
	);
};
